/**
 * useEvaluationSearch — React Query wrapper around GET /api/evaluations/search.
 *
 * Accepts a full EvaluationSearchParams object (Tier 1 + Tier 2 fields) and
 * returns a stable result object with items, total, hasMore, loading, error.
 *
 * The query is disabled until a user is authenticated.
 * Tier-2 params (score_min, score_max, video_status, body_part) are passed
 * through even if the backend index is not yet live — the API degrades
 * gracefully by ignoring unknown query params.
 */

import { useQuery } from '@tanstack/react-query';
import { searchEvaluations } from '@/services/evaluationSearchApi';
import type {
  EvaluationSearchParams,
  EvaluationSearchItem,
} from '@/services/evaluationSearchApi';
import { useAuth } from '@/contexts/AuthContext';

export interface UseEvaluationSearchResult {
  items: EvaluationSearchItem[];
  total: number;
  hasMore: boolean;
  loading: boolean;
  error: Error | null;
}

/**
 * Search evaluations with filters, sort, and pagination.
 *
 * @param params - All supported search params (Tier 1 + Tier 2).
 *   If undefined, the query fetches with default server behaviour (most recent
 *   20 evals for the current user).
 */
export function useEvaluationSearch(
  params?: EvaluationSearchParams
): UseEvaluationSearchResult {
  const { user } = useAuth();

  const { data, isLoading, error } = useQuery({
    // Include all params in the query key so any filter change triggers a
    // fresh fetch. JSON.stringify produces a stable string for objects/arrays.
    queryKey: ['evaluation-search', user?.id, JSON.stringify(params ?? {})],
    queryFn: () => searchEvaluations(params),
    enabled: !!user?.id,
  });

  return {
    items: data?.items ?? [],
    total: data?.total ?? 0,
    hasMore: data?.has_more ?? false,
    loading: isLoading,
    error: error as Error | null,
  };
}
